import { Suspense, lazy, useCallback, useMemo } from 'react'
import { Checkbox, Group, SegmentedControl, Select, Text } from '@mantine/core'
import {
  applyLapFilter,
  countsTowardsAverage,
  defaultAverageOptions,
  driversInLaps,
  everyLap,
  formatLapTime,
  lapFlags,
  lapStats,
  parseAverageOptions,
  parseLapFilter,
  baselineLabels,
  bestOf,
  compare as compareTimes,
  formatDelta,
  resolveDriver,
  stintsInLaps,
  stintVendorNames,
  type AverageOptions,
  type Baseline,
  type CarSummaryDoc,
  type LapDoc,
  type LapFilter,
  type Measure,
} from '@racedash/shared'
import type { DashboardData } from '../../dashboard/useSessionData'
import { describeDriver, type ScreenContext } from '../../dashboard/screenContext'
import { useLocalSetting } from '../../lib/useLocalSetting'
import { usePortalTarget } from '../../lib/usePortalTarget'
import { useCarsLaps } from '../../dashboard/useCarsLaps'
import { useFieldLaps } from '../../dashboard/useFieldLaps'
import CarPicker from '../../components/CarPicker'
import { useEventSummaries, useSessionSummaries } from '../../dashboard/useCarSummaries'
import './laps.css'

/**
 * The charts, and the charting library with them, kept out of the main bundle.
 *
 * Everything else on this screen is a table. A share-link viewer looking at
 * lap times should not download a hundred kilobytes of chart code to read one.
 */
const LapGraphs = lazy(() => import('./LapGraphs'))
const BestTimes = lazy(() => import('./BestTimes'))

/*
 * The screen's sub-pages.
 *
 * Split from the chart type because they are different questions. "Am I looking
 * at numbers or at a shape" is one choice; "which shape" is another, and only
 * exists once the first is answered. Flattening them into one control meant a
 * row of buttons that grew every time a chart was added and read as a list of
 * unrelated things — and it left no room for a sub-page that is neither, which
 * is what Best Times will be.
 */
type Page = 'stats' | 'charts' | 'best'

/** Which chart, once Charts is the page. */
type Chart = 'lap' | 'rising' | 'gap' | 'position' | 'pace'

/** How the best-times columns are written. Mirrors BestTimes' own Mode. */
type BestMode = 'time' | 'toBest' | `to:${string}`

/** What the rising-average chart is comparing. */
type Compare = 'drivers' | 'cars'

/** Whose positions the position chart draws. */
type Scope = 'car' | 'cars' | 'field'

/** Which reading of pace against the field. */
type PaceMode = 'bestSoFar' | 'window'
const binChoices = [10, 20, 30] as const

/**
 * Every lap for one car, and what comes out of them.
 *
 * Three readings of one set of laps — the record, the shape of it, and the
 * consistency behind it — over one set of controls. They are views rather than
 * separate screens because the filters mean the same thing in all three, and
 * splitting them would mean picking a stint again every time you wanted to see
 * it drawn instead of listed.
 *
 * Everything is computed over the same filtered set, so narrowing to a stint or
 * a driver narrows the numbers and the lines with it. A card that quietly kept
 * averaging the whole race would be worse than no card.
 *
 * Live, because it is left open during one. The laps come off a snapshot
 * listener, so a lap completed while you are looking at this appears at the top
 * and moves the averages and the curves with it.
 */
export default function LapsScreen({
  data,
  car,
  context,
  screenId,
  sessionId,
}: {
  data: DashboardData
  car: string | null
  context: ScreenContext
  screenId: string
  sessionId: string | null
}) {
  const key = `racedash.laps.${context.viewId}.${screenId}`
  // Migrated on read from the flat three-way setting this replaced, so a
  // dashboard that was left showing the rising average still opens on it.
  const [page, setPage] = useLocalSetting<Page>(`${key}.page`, 'stats', (raw) =>
    raw === 'best' ? 'best' : raw === 'charts' || raw === 'lap' || raw === 'rising' ? 'charts' : 'stats',
  )
  const [chart, setChart] = useLocalSetting<Chart>(`${key}.chart`, 'lap', (raw) =>
    raw === 'rising' ? 'rising' : raw === 'gap' ? 'gap' : raw === 'position' ? 'position' : raw === 'pace' ? 'pace' : 'lap',
  )
  const [paceMode, setPaceMode] = useLocalSetting<PaceMode>(`${key}.paceMode`, 'bestSoFar', (raw) =>
    raw === 'window' ? 'window' : 'bestSoFar',
  )
  const [binMinutes, setBinMinutes] = useLocalSetting<number>(`${key}.paceBin`, 20, (raw) =>
    typeof raw === 'number' && (binChoices as readonly number[]).includes(raw) ? raw : 20,
  )
  const [scope, setScope] = useLocalSetting<Scope>(`${key}.scope`, 'car', (raw) =>
    raw === 'cars' ? 'cars' : raw === 'field' ? 'field' : 'car',
  )
  const [compare, setCompare] = useLocalSetting<Compare>(`${key}.compare`, 'drivers', (raw) =>
    raw === 'cars' ? 'cars' : 'drivers',
  )
  const [others, setOthers] = useLocalSetting<string[]>(`${key}.others`, [], (raw) =>
    Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [],
  )
  const [filter, setFilter] = useLocalSetting<LapFilter>(`${key}.filter`, everyLap, parseLapFilter)
  const [options, setOptions] = useLocalSetting<AverageOptions>(
    `${key}.average`,
    defaultAverageOptions,
    parseAverageOptions,
  )

  /**
   * Who was driving, resolved the same way the stint chart resolves it.
   *
   * At the lap's START, because a driver change happens in the pits: the person
   * who began the lap drove it. Resolving at the end would attribute an in-lap
   * to whoever got in afterwards.
   */
  // The vendor's name per stint rather than per lap: the per-lap field is
  // whoever the desk had selected at the time, and at the start of a race that
  // was three different people in five laps. See stintVendorNames.
  const settledNames = useMemo(() => stintVendorNames(data.laps), [data.laps])
  const driverOf = useCallback(
    (lap: LapDoc): string | null => {
      const at = lap.startedAt?.toDate() ?? lap.completedAt?.toDate()
      const vendor = lap.stintNumber != null ? (settledNames.get(lap.stintNumber) ?? lap.driver) : lap.driver
      if (!at || !car) return vendor ?? null
      return describeDriver(context, car, resolveDriver(context.declarations, car, at, vendor ?? undefined)).name
    },
    [context, car, settledNames],
  )

  const laps = data.laps
  const stints = useMemo(() => stintsInLaps(laps), [laps])
  const drivers = useMemo(() => driversInLaps(laps, driverOf), [laps, driverOf])

  const shown = useMemo(
    () => applyLapFilter(laps, filter, driverOf),
    [laps, filter, driverOf],
  )
  const stats = useMemo(() => lapStats(shown, options), [shown, options])

  // Only fetched for the chart that compares cars, and only for the cars
  // actually chosen — every one is a listener of its own.
  const wantsOthers =
    page === 'charts' &&
    (chart === 'gap' || (chart === 'rising' && compare === 'cars') || (chart === 'position' && scope === 'cars'))
  const otherLaps = useCarsLaps(wantsOthers ? sessionId : null, others)
  // The whole field, for the race chart alone. Every other chart is a handful
  // of cars; this one is all of them, and is the only listener of its size.
  const wantsField = page === 'charts' && ((chart === 'position' && scope === 'field') || chart === 'pace')
  const field = useFieldLaps(wantsField ? sessionId : null)

  // One line per car, in number order so the colours are stable from one
  // render to the next. Shared by the race chart and the pace charts.
  const fieldSeries = useMemo(() => {
    const byCar = new Map<string, LapDoc[]>()
    for (const l of field.laps) {
      const list = byCar.get(l.car)
      if (list) list.push(l)
      else byCar.set(l.car, [l])
    }
    return [...byCar.entries()]
      .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
      .map(([n, ls]) => ({ key: `car-${n}`, label: `Car ${n}`, laps: ls }))
  }, [field.laps])

  // When this car last crossed the line: the moment the pace sentence is about.
  const stoppedAt = useMemo(() => {
    let last: number | null = null
    for (const l of laps) {
      const at = l.completedAt?.toDate().getTime()
      if (at != null && (last == null || at > last)) last = at
    }
    return last
  }, [laps])

  /**
   * The lines to draw.
   *
   * Built from the FILTERED laps for this car, so narrowing to a stint narrows
   * the chart with it — the same rule the cards follow, and the reason all
   * three views can share one set of controls.
   */
  const series = useMemo(() => {
    if (page === 'charts' && ((chart === 'position' && scope === 'field') || chart === 'pace')) {
      return fieldSeries
    }
    if (page === 'charts' && chart === 'position' && scope === 'cars') {
      return [
        { key: 'self', label: `Car ${car}`, laps: shown },
        ...others.map((n) => ({ key: `car-${n}`, label: `Car ${n}`, laps: otherLaps.get(n) ?? [] })),
      ]
    }
    if (page === 'charts' && chart === 'gap') {
      // The reference car is not a series here — it IS the axis — so only the
      // others are drawn.
      return others.map((n) => ({ key: `car-${n}`, label: `Car ${n}`, laps: otherLaps.get(n) ?? [] }))
    }
    if (page === 'charts' && chart === 'rising' && compare === 'cars') {
      return [
        { key: 'self', label: `Car ${car}`, laps: shown },
        ...others.map((n) => ({ key: `car-${n}`, label: `Car ${n}`, laps: otherLaps.get(n) ?? [] })),
      ]
    }
    if (page === 'charts' && chart === 'rising' && drivers.length > 1) {
      // One line per driver, which is the comparison people actually want out
      // of this chart: two drivers in one car, over the same track and the same
      // conditions, is as close to a controlled experiment as racing gets.
      return drivers.map((d) => ({
        key: `drv-${d}`,
        label: d,
        laps: shown.filter((l) => driverOf(l) === d),
      }))
    }
    return [{ key: 'self', label: car ? `Car ${car}` : 'Laps', laps: shown }]
  }, [page, chart, compare, scope, shown, others, otherLaps, fieldSeries, drivers, driverOf, car])

  // The lines the position chart draws heavy: this car, and every car the
  // team runs, whichever key it is under.
  const emphasis = useMemo(
    () => ['self', ...context.ourCars.map((n) => `car-${n}`), ...(car ? [`car-${car}`] : [])],
    [context.ourCars, car],
  )

  /*
   * What the figures on the cards are held against.
   *
   * A lap time alone says very little — two minutes fourteen is quick or slow
   * depending on the circuit, the class and the fuel load — and the number
   * anybody acts on is the difference. Kept in this browser rather than on the
   * dashboard: which comparison is useful is a question about the person
   * looking, not about the dashboard.
   */
  const [baseline, setBaseline] = useLocalSetting<Baseline>(`${key}.baseline`, 'session', (raw) =>
    raw === 'class' || raw === 'event' ? raw : 'session',
  )

  const portal = usePortalTarget()
  const [bestMode, setBestMode] = useLocalSetting<BestMode>(`${key}.bestMode`, 'time', (raw) =>
    typeof raw === 'string' && (raw === 'time' || raw === 'toBest' || raw.startsWith('to:'))
      ? (raw as BestMode)
      : 'time',
  )
  const [bestClass, setBestClass] = useLocalSetting<string | null>(`${key}.bestClass`, null, (raw) =>
    typeof raw === 'string' && raw ? raw : null,
  )
  const [bestView, setBestView] = useLocalSetting<'list' | 'spread'>(`${key}.bestView`, 'list', (raw) =>
    raw === 'spread' ? 'spread' : 'list',
  )

  const sessionSummaries = useSessionSummaries(sessionId)
  const eventSummaries = useEventSummaries(data.session?.eventId ?? null, car)

  const compareTo = useMemo(() => {
    const mine = sessionSummaries.find((s) => s.car === car) ?? null
    if (baseline === 'session') return mine ? [mine] : []
    if (baseline === 'event') return eventSummaries
    // Best in class means the class this car is in. Without one there is no
    // meaningful peer group, so it falls back to the car's own session — which
    // is honest rather than silently comparing against the outright fastest car
    // in a different class doing a different job.
    const klass = mine?.class
    if (!klass) return mine ? [mine] : []
    return sessionSummaries.filter((s) => s.class === klass)
  }, [baseline, sessionSummaries, eventSummaries, car])

  // Everything on track except the car this screen is already showing.
  const otherCars = useMemo(
    () =>
      (data.standings?.cars ?? [])
        .filter((c) => c.number && c.number !== car)
        .map((c) => ({
          number: c.number,
          klass: c.class,
          ours: context.ourCars.includes(c.number),
        })),
    [data.standings, car, context.ourCars],
  )

  if (!car) {
    return (
      <div className="laps-empty">
        <p>Pick a car to see its laps.</p>
      </div>
    )
  }

  if (laps.length === 0) {
    return (
      <div className="laps-empty">
        <p>No laps recorded for car {car} yet.</p>
      </div>
    )
  }

  return (
    <div className="laps">
      <Group gap="sm" wrap="wrap" align="center" className="laps-controls">
        {stints.length > 1 && (
          <Select
            aria-label="Stint"
            size="xs"
            w={260}
            allowDeselect={false}
            data={[
              { value: '', label: 'All stints' },
              // Displayed one-based, as everywhere else: RIS counts stints from
              // zero and nobody in a garage does. Named and numbered, because
              // "Stint 4" identifies nothing on its own — who was in it and
              // which laps it covers is how anybody actually refers to it.
              ...stints.map((n) => ({ value: String(n), label: stintLabel(n, laps, driverOf) })),
            ]}
            value={filter.stint == null ? '' : String(filter.stint)}
            onChange={(v) => setFilter({ ...filter, stint: v === '' || v == null ? null : Number(v) })}
          />
        )}

        {drivers.length > 1 && (
          <Select
            aria-label="Driver"
            size="xs"
            w={180}
            allowDeselect={false}
            data={[
              { value: '', label: 'All drivers' },
              ...drivers.map((d) => ({ value: d, label: d })),
            ]}
            value={filter.driver ?? ''}
            onChange={(v) => setFilter({ ...filter, driver: v || null })}
          />
        )}

        <Text size="xs" c="dimmed">
          {shown.length} of {laps.length} laps
        </Text>

        {page === 'stats' && (
          <Select
            aria-label="Compare against"
            size="xs"
            w={210}
            allowDeselect={false}
            data={[
              { value: 'session', label: 'vs this session' },
              { value: 'class', label: 'vs best in class' },
              { value: 'event', label: 'vs the whole event' },
            ]}
            value={baseline}
            onChange={(v) => v && setBaseline(v as Baseline)}
            comboboxProps={{ portalProps: { target: portal } }}
          />
        )}

        <SegmentedControl
          size="xs"
          value={page}
          onChange={(v) => setPage(v as Page)}
          data={[
            { value: 'stats', label: 'Statistics' },
            { value: 'charts', label: 'Charts' },
            { value: 'best', label: 'Best times' },
          ]}
        />

        {page === 'charts' && (
          <SegmentedControl
            size="xs"
            value={chart}
            onChange={(v) => setChart(v as Chart)}
            data={[
              { value: 'lap', label: 'Lap times' },
              { value: 'rising', label: 'Rising average' },
              { value: 'gap', label: 'Gap' },
              { value: 'position', label: 'Position' },
              { value: 'pace', label: 'Pace vs field' },
            ]}
          />
        )}

        {page === 'charts' && chart === 'pace' && (
          <SegmentedControl
            size="xs"
            value={paceMode}
            onChange={(v) => setPaceMode(v as PaceMode)}
            data={[
              { value: 'bestSoFar', label: 'Best so far' },
              { value: 'window', label: 'Best in window' },
            ]}
          />
        )}

        {page === 'charts' && chart === 'pace' && paceMode === 'window' && (
          <SegmentedControl
            size="xs"
            value={String(binMinutes)}
            onChange={(v) => setBinMinutes(Number(v))}
            data={binChoices.map((m) => ({ value: String(m), label: `${m} min` }))}
          />
        )}

        {page === 'charts' && chart === 'position' && (
          <SegmentedControl
            size="xs"
            value={scope}
            onChange={(v) => setScope(v as Scope)}
            data={[
              { value: 'car', label: 'This car' },
              { value: 'cars', label: 'Chosen cars' },
              { value: 'field', label: 'Whole field' },
            ]}
          />
        )}

        {page === 'charts' && chart === 'rising' && (
          <SegmentedControl
            size="xs"
            value={compare}
            onChange={(v) => setCompare(v as Compare)}
            data={[
              { value: 'drivers', label: 'By driver' },
              { value: 'cars', label: 'By car' },
            ]}
          />
        )}

        {page === 'charts' && wantsOthers && (
          <CarPicker
            label="Compare with"
            cars={otherCars}
            value={others}
            onChange={setOthers}
            // Five other cars plus your own. More lines than that is a chart
            // nobody can read, and each one is a listener of its own.
            max={5}
          />
        )}
      </Group>

      {/* A chart gets the whole width. The cards were kept beside it on the
          theory that the numbers are useful whatever is on screen; in practice
          they squeezed the one thing the page had been switched to in order to
          look at. Numbers belong on the numbers page. */}
      <div className="laps-body" data-page={page}>
        {page === 'best' ? (
          <Suspense fallback={<div className="laps-empty">Loading…</div>}>
            <BestTimes
              summaries={sessionSummaries}
              ourCars={context.ourCars}
              mode={bestMode}
              onMode={setBestMode}
              klass={bestClass}
              onClass={setBestClass}
              view={bestView}
              onView={setBestView}
            />
          </Suspense>
        ) : page === 'charts' ? (
          <div className="laps-chart-wrap">
            <Suspense fallback={<div className="laps-empty">Loading the chart…</div>}>
              <LapGraphs
                view={chart === 'pace' ? paceMode : chart}
                series={series}
                options={options}
                showBands={filter.stint == null}
                reference={car ? { key: 'self', label: `Car ${car}`, laps: shown } : undefined}
                emphasis={emphasis}
                pace={{ ourKey: `car-${car}`, ourLabel: `Car ${car}`, stoppedAt, binMs: binMinutes * 60_000 }}
                note={
                  field.error
                    ? `The field's laps could not be read: ${field.error}`
                    : field.loading
                      ? 'Loading every lap of the session…'
                      : chart === 'position' && scope === 'field' && field.truncated
                        ? 'Not the whole race: the field has more laps than this chart will load, so it stops early.'
                        : null
                }
              />
            </Suspense>
          </div>
        ) : (
        <div className="laps-table-wrap">
          <table className="laps-table">
            <thead>
              <tr>
                <th className="num">Lap</th>
                <th className="num">Time</th>
                <th className="num">S1</th>
                <th className="num">S2</th>
                <th className="num">S3</th>
                <th>Driver</th>
                <th className="num">St</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((lap) => (
                <LapRow
                  key={lap.lap}
                  lap={lap}
                  driver={driverOf(lap)}
                  bestLap={stats.best?.lap}
                  bestSectorLaps={[
                    stats.sectors[0]?.lap,
                    stats.sectors[1]?.lap,
                    stats.sectors[2]?.lap,
                  ]}
                  counted={countsTowardsAverage(lap, options)}
                />
              ))}
            </tbody>
          </table>
        </div>
        )}

        {page === 'stats' && (
        <div className="laps-cards">
          <Card title="Best lap">
            <div className="laps-big">{formatLapTime(stats.best?.ms ?? null)}</div>
            <div className="laps-sub">{stats.best ? `Lap ${stats.best.lap}` : 'No timed laps'}</div>
            <Against ours={stats.best?.ms ?? null} set={compareTo} measure="lap" baseline={baseline} />
          </Card>

          <Card title="Average">
            <div className="laps-big">{formatLapTime(stats.averageMs)}</div>
            {/* Always says what it counted. An average over 12 of 15 laps is a
                different claim from one over all 15, and the difference is
                exactly what the exclusions are for. */}
            <div className="laps-sub">
              {stats.counted} of {stats.considered} laps
            </div>
            <div className="laps-toggles">
              <Checkbox
                size="xs"
                label="Exclude in and out laps"
                checked={options.excludePitLaps}
                onChange={(e) =>
                  setOptions({ ...options, excludePitLaps: e.currentTarget.checked })
                }
              />
              <Checkbox
                size="xs"
                label="Exclude yellows and slow zones"
                checked={options.excludeNeutralised}
                onChange={(e) =>
                  setOptions({ ...options, excludeNeutralised: e.currentTarget.checked })
                }
              />
            </div>
          </Card>

          {/* Best sectors and the ideal in one card, because the ideal is
              nothing but their sum and separating them would make somebody
              add up three numbers to check it. */}
          <Card title="Best sectors">
            <div className="laps-sectors">
              {stats.sectors.map((s, i) => (
                <div className="laps-sector" key={i}>
                  <div className="laps-sector-label">S{i + 1}</div>
                  <div className="laps-sector-time">{formatLapTime(s?.ms ?? null)}</div>
                  <div className="laps-sector-lap">{s ? `Lap ${s.lap}` : '—'}</div>
                  <Against
                    ours={s?.ms ?? null}
                    set={compareTo}
                    measure={(['s1', 's2', 's3'] as const)[i] ?? 's1'}
                    baseline={baseline}
                    compact
                  />
                </div>
              ))}
            </div>
            <div className="laps-ideal">
              <span className="laps-ideal-label">Ideal lap</span>
              <span className="laps-big">{formatLapTime(stats.idealMs)}</span>
              {/* The number anybody acts on. The ideal on its own is a curiosity;
                  what it is worth against the best real lap is a decision. */}
              {stats.gainMs != null && (
                <span className="laps-gain">{formatLapTime(stats.gainMs)} in hand</span>
              )}
            </div>
            <Against ours={stats.idealMs} set={compareTo} measure="ideal" baseline={baseline} />
          </Card>
        </div>
        )}
      </div>
    </div>
  )
}

/**
 * How a stint reads in the picker: number, driver, laps.
 *
 * The driver only when one person did the whole stint. A stint the feed
 * attributes to two people usually means a change reported late rather than two
 * drivers, and naming one of them would assert something we do not know.
 */
function stintLabel(
  n: number,
  laps: readonly LapDoc[],
  driverOf: (lap: LapDoc) => string | null,
): string {
  const inStint = laps.filter((l) => l.stintNumber === n)
  if (inStint.length === 0) return `Stint ${n + 1}`

  const numbers = inStint.map((l) => l.lap)
  const from = Math.min(...numbers)
  const to = Math.max(...numbers)
  const range = from === to ? `lap ${from}` : `laps ${from}–${to}`

  const named = new Set(inStint.map(driverOf).filter((d): d is string => !!d))
  const who = named.size === 1 ? `${[...named][0]}, ` : ''
  return `Stint ${n + 1} — ${who}${range}`
}

/**
 * One figure held against its baseline.
 *
 * Shows nothing at all when there is nothing to compare to, and nothing when
 * the baseline IS this figure — comparing a car's best lap against its own
 * session best is a guaranteed "±0.000" that teaches nobody anything and makes
 * the card look like it is working when it is not.
 */
function Against({
  ours,
  set,
  measure,
  baseline,
  compact,
}: {
  ours: number | null
  set: readonly CarSummaryDoc[]
  measure: Measure
  baseline: Baseline
  compact?: boolean
}) {
  const best = bestOf(set, measure)
  const c = compareTimes(ours, best?.ms ?? null, baselineLabels[baseline])
  if (c.deltaMs == null || c.isSelf) return null

  return (
    <div className="laps-against" data-compact={compact || undefined}>
      <span className="laps-against-delta" data-quicker={c.deltaMs < 0 || undefined}>
        {formatDelta(c.deltaMs)}
      </span>
      {!compact && (
        <span className="laps-against-what">
          {formatLapTime(c.ms)} · {c.label}
          {/* Whose lap it was, when the baseline is somebody else's. */}
          {baseline === 'class' && best?.from.car ? ` (#${best.from.car})` : ''}
        </span>
      )}
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="laps-card">
      <h3 className="laps-card-title">{title}</h3>
      {children}
    </section>
  )
}

function LapRow({
  lap,
  driver,
  bestLap,
  bestSectorLaps,
  counted,
}: {
  lap: LapDoc
  driver: string | null
  bestLap?: number
  bestSectorLaps: (number | undefined)[]
  counted: boolean
}) {
  const flags = lapFlags(lap)
  // Why a lap is not in the average, on the row rather than only in the total.
  // "12 of 15" tells you three were dropped; this tells you which three. A lap
  // can be both in and out, a drive-through most often, and saying only IN
  // made two penalties on consecutive laps read as two in-laps in a row, which
  // cannot happen.
  const note = [flags.outLap && 'OUT', flags.inLap && 'IN'].filter(Boolean).join('+') || (flags.neutralised ? 'FCY' : '')

  return (
    <tr data-excluded={!counted || undefined}>
      <td className="num">{lap.lap}</td>
      <td className="num" data-best={lap.lap === bestLap || undefined}>
        {formatLapTime(lap.lapTimeMs)}
      </td>
      <td className="num" data-best={lap.lap === bestSectorLaps[0] || undefined}>
        {formatLapTime(lap.s1Ms)}
      </td>
      <td className="num" data-best={lap.lap === bestSectorLaps[1] || undefined}>
        {formatLapTime(lap.s2Ms)}
      </td>
      <td className="num" data-best={lap.lap === bestSectorLaps[2] || undefined}>
        {formatLapTime(lap.s3Ms)}
      </td>
      <td className="laps-driver">{driver ?? '—'}</td>
      <td className="num">{lap.stintNumber == null ? '—' : lap.stintNumber + 1}</td>
      <td className="laps-flag">{note}</td>
    </tr>
  )
}
